import React, { useState, useEffect, useCallback } from 'react';
import { 
  PencilIcon, 
  EyeIcon, 
  XMarkIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

export default function TemplatesManagement() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'info' });
  const [activeCategory, setActiveCategory] = useState('All');

  // Group templates by category
  const templatesByCategory = templates.reduce((acc, template) => {
    const category = template.category || 'General';
    if (!acc[category]) acc[category] = [];
    acc[category].push(template);
    return acc;
  }, {});

  const categories = ['All', ...Object.keys(templatesByCategory)];
  
  const filteredTemplates = activeCategory === 'All' 
    ? templates 
    : templatesByCategory[activeCategory] || [];


  const handleEditTemplate = async (template) => {
    try {
      const response = await axios.get(`/api/platform/templates/${template.name}`);
      setSelectedTemplate({ ...template, ...response.data });
      setEditedContent(response.data.content);
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Error fetching template content:', error);
      showNotification('Failed to load template content', 'error');
    }
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      setSaving(true);
      await axios.put(`/api/platform/templates/${selectedTemplate.name}`, {
        content: editedContent
      });
      
      showNotification('Template updated successfully', 'success');
      setEditDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      showNotification('Failed to update template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewTemplate = async (template) => {
    try {
      setPreviewing(true);
      const response = await axios.post(`/api/platform/templates/${template.name}/preview`);
      setPreviewHtml(response.data.html);
      setSelectedTemplate(template);
      setPreviewDialogOpen(true);
    } catch (error) {
      console.error('Error previewing template:', error);
      showNotification('Failed to preview template', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'info' }), 4000);
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/platform/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      showNotification('Failed to fetch templates', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const getCategoryBadgeColor = (category) => {
    const colors = {
      'Reservations': 'bg-blue-100 text-blue-800',
      'Invitations': 'bg-purple-100 text-purple-800',
      'Notifications': 'bg-green-100 text-green-800',
      'General': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        <span className="ml-3 text-gray-600">Loading templates...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Email Templates</h2>
        <p className="text-gray-600">
          Manage and customize email templates used throughout the platform. Click edit to modify content or preview to see how they appear to recipients.
        </p>
      </div>

      {/* Category Filter */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {categories.map((category) => {
            const count = category === 'All' ? templates.length : (templatesByCategory[category]?.length || 0);
            return (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeCategory === category
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {category} ({count})
              </button>
            );
          })}
        </nav>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <div key={template.name} className="bg-white rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow">
            <div className="p-6">
              {/* Header with badge */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center space-x-2">
                  <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                  <h3 className="text-lg font-medium text-gray-900">
                    {template.displayName}
                  </h3>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryBadgeColor(template.category)}`}>
                  {template.category}
                </span>
              </div>
              
              {/* Description */}
              <p className="text-sm text-gray-600 mb-4">
                {template.description}
              </p>

              {/* Variables */}
              {template.variables && template.variables.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-1">Available Variables:</p>
                  <p className="text-xs text-gray-400 break-words">
                    {template.variables.map(variable => `{{${variable}}}`).join(', ')}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditTemplate(template)}
                  className="flex items-center px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => handlePreviewTemplate(template)}
                  disabled={previewing}
                  className="flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm font-medium disabled:opacity-50"
                >
                  {previewing ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-500 border-t-transparent mr-1"></div>
                  ) : (
                    <EyeIcon className="h-4 w-4 mr-1" />
                  )}
                  Preview
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No templates</h3>
          <p className="mt-1 text-sm text-gray-500">
            {activeCategory === 'All' ? 'No email templates found.' : `No templates found in ${activeCategory} category.`}
          </p>
        </div>
      )}

      {/* Edit Modal */}
      {editDialogOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Edit Template: {selectedTemplate?.displayName}
              </h3>
              <button
                onClick={() => setEditDialogOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                rows={20}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                placeholder="Enter template content using Handlebars syntax..."
              />
              
              {selectedTemplate?.variables && (
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="font-medium text-gray-900 text-sm mb-1">Available Variables:</p>
                  <p className="text-sm text-gray-600 break-words">
                    {selectedTemplate.variables.map(variable => `{{${variable}}}`).join(', ')}
                  </p>
                </div>
              )}
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setEditDialogOpen(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  disabled={saving}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center"
                >
                  {saving && (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  )}
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewDialogOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Preview: {selectedTemplate?.displayName}
              </h3>
              <button
                onClick={() => setPreviewDialogOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div 
              className="border border-gray-200 rounded-md p-4 bg-white max-h-96 overflow-auto"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
            
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setPreviewDialogOpen(false)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-lg z-50 ${
          notification.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
          notification.type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
          'bg-blue-100 text-blue-800 border border-blue-200'
        }`}>
          <div className="flex items-center">
            <p className="text-sm font-medium">{notification.message}</p>
            <button
              onClick={() => setNotification({ show: false, message: '', type: 'info' })}
              className="ml-3 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}